import { useEffect, useMemo, useRef, useState } from 'react'
import { getPredictionsCached, groupPredictions, prettifyPredictionName } from '@astro/vedastro'
import { CardsSkeleton, PLANET_GLYPHS, StudioError } from '../StudioBits'

/**
 * Life Report — every classical prediction the engine finds in the chart,
 * grouped into life areas. One cached call shared with the Yogas part.
 */
export default function ReportPart({ birth }) {
  const [status, setStatus] = useState('loading') // loading | error | done
  const [predictions, setPredictions] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [groupKey, setGroupKey] = useState('all')
  const reqRef = useRef(0)

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    getPredictionsCached({ dob: birth.dob, tob: birth.tob, location: birth.location })
      .then((list) => {
        if (my !== reqRef.current) return
        setPredictions(list)
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not compile the report right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => (predictions ? groupPredictions(predictions) : []), [predictions])
  const shown = groupKey === 'all' ? groups : groups.filter((g) => g.key === groupKey)
  const total = groups.reduce((a, g) => a + g.items.length, 0)

  if (status === 'loading') return <CardsSkeleton cards={6} />
  if (status === 'error') return <StudioError text={errorMsg} onRetry={load} />

  return (
    <div>
      <div className="ks-report-chips">
        <button
          type="button"
          className={`ks-chip${groupKey === 'all' ? ' active' : ''}`}
          onClick={() => setGroupKey('all')}
        >
          ✦ All · {total}
        </button>
        {groups.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`ks-chip${groupKey === g.key ? ' active' : ''}`}
            onClick={() => setGroupKey(g.key)}
          >
            {g.icon} {g.label} · {g.items.length}
          </button>
        ))}
      </div>

      {shown.map((g) => (
        <div key={g.key}>
          <p className="ks-group-title">{g.icon} {g.label} · {g.items.length}</p>
          <div className="ks-card-grid">
            {g.items.map((p) => (
              <div className="ks-card" key={p.name}>
                <div className="ks-card-top">
                  <span className="ks-card-icon">{g.icon}</span>
                  {p.planets?.length > 0 && (
                    <span className="ks-card-planets">{p.planets.map((pl) => PLANET_GLYPHS[pl] || pl).join(' ')}</span>
                  )}
                </div>
                <h4>{prettifyPredictionName(p.name)}</h4>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="ks-note">
        {total} classical readings drawn from your planetary positions, house lords and yogas. Individual results
        blend and balance each other — for a woven-together interpretation, sit with Maharaj Ji.
      </p>
    </div>
  )
}
